import bbox from '@turf/bbox';
import booleanIntersects from '@turf/boolean-intersects';
import centroid from '@turf/centroid';

// ---------------------------------------------------------------------------
// Zone selection constraint constants
// ---------------------------------------------------------------------------

/** Hard maximum of ZIP codes allowed per subscription (all tiers). */
export const MAX_ZIP_CODES_ABSOLUTE = 200;

/** Maximum allowed span (straight-line distance between the two farthest centroids)
 *  across the entire selection. Prevents snake-shaped selections. */
export const MAX_SELECTION_EXTENT_KM = 50;

// ---------------------------------------------------------------------------
// Geometry utilities (polygon-based, uses loaded GeoJSON)
// ---------------------------------------------------------------------------

/**
 * Haversine distance in kilometres between two [lat, lon] points.
 * Internal helper — not exported.
 */
const haversineKm = (a: [number, number], b: [number, number]): number => {
    const R = 6371;
    const dLat = (b[0] - a[0]) * (Math.PI / 180);
    const dLon = (b[1] - a[1]) * (Math.PI / 180);
    const lat1 = a[0] * (Math.PI / 180);
    const lat2 = b[0] * (Math.PI / 180);
    const x =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

/**
 * Returns true if at least one polygon in `candidateZips` geometrically
 * touches or overlaps a polygon from `selectedZips` in the provided GeoJSON
 * FeatureCollection.
 *
 * Uses the same two-phase approach as MapComponent: bbox pre-filter (±0.02°)
 * followed by exact turf.booleanIntersects check.
 *
 * Permissive (returns true) when polygonData is null or when no features are
 * found for either side.
 */
export const isPolygonNeighbour = (
    candidateZips: string[],
    selectedZips: string[],
    polygonData: any | null
): boolean => {
    if (!polygonData) return true;

    const candidateFeatures = polygonData.features.filter((f: any) =>
        candidateZips.includes(f.properties?.plz)
    );
    const selectedFeatures = polygonData.features.filter((f: any) =>
        selectedZips.includes(f.properties?.plz)
    );

    if (candidateFeatures.length === 0 || selectedFeatures.length === 0) return true;

    for (const cf of candidateFeatures) {
        const cfBox = bbox(cf);
        for (const sf of selectedFeatures) {
            const sfBox = bbox(sf);
            const expSfBox = [sfBox[0] - 0.02, sfBox[1] - 0.02, sfBox[2] + 0.02, sfBox[3] + 0.02];

            if (cfBox[0] > expSfBox[2] || cfBox[2] < expSfBox[0] ||
                cfBox[1] > expSfBox[3] || cfBox[3] < expSfBox[1]) {
                continue;
            }

            try {
                if (booleanIntersects(sf, cf)) return true;
            } catch {
                return true; // Geometry error — be permissive
            }
        }
    }
    return false;
};

/**
 * Returns true if all ZIPs in `zips` form a single connected component based
 * on polygon adjacency (turf.booleanIntersects with bbox pre-filter).
 *
 * Permissive (returns true) when polygonData is null or fewer than 2 ZIPs
 * have polygon data.
 */
export const isPolygonConnected = (
    zips: string[],
    polygonData: any | null
): boolean => {
    if (!polygonData || zips.length <= 1) return true;

    const features = zips.flatMap((zip) => {
        const f = polygonData.features.find((feat: any) => feat.properties?.plz === zip);
        return f ? [f] : [];
    });

    if (features.length < 2) return true;

    const visited = new Set<string>([features[0].properties.plz]);
    const queue = [features[0]];

    while (queue.length > 0) {
        const current = queue.shift()!;
        const curBox = bbox(current);

        for (const f of features) {
            const plz = f.properties.plz;
            if (visited.has(plz)) continue;

            const fBox = bbox(f);
            const expCurBox = [curBox[0] - 0.02, curBox[1] - 0.02, curBox[2] + 0.02, curBox[3] + 0.02];

            if (fBox[0] > expCurBox[2] || fBox[2] < expCurBox[0] ||
                fBox[1] > expCurBox[3] || fBox[3] < expCurBox[1]) {
                continue;
            }

            try {
                if (booleanIntersects(current, f)) {
                    visited.add(plz);
                    queue.push(f);
                }
            } catch {
                visited.add(plz);
                queue.push(f);
            }
        }
    }

    return visited.size === features.length;
};

/**
 * Computes the maximum span (km) of the given ZIP selection: the straight-line
 * distance between the two farthest polygon centroids.
 *
 * Returns 0 when polygonData is null or fewer than 2 ZIPs have polygon data.
 */
export const getPolygonSelectionExtent = (
    zips: string[],
    polygonData: any | null
): number => {
    if (!polygonData || zips.length < 2) return 0;

    const centroids: [number, number][] = zips.flatMap((zip) => {
        const f = polygonData.features.find((feat: any) => feat.properties?.plz === zip);
        if (!f) return [];
        try {
            const c = centroid(f);
            const [lon, lat] = c.geometry.coordinates;
            return [[lat, lon] as [number, number]];
        } catch {
            return [];
        }
    });

    if (centroids.length < 2) return 0;

    let maxDist = 0;
    for (let i = 0; i < centroids.length; i++) {
        for (let j = i + 1; j < centroids.length; j++) {
            const d = haversineKm(centroids[i], centroids[j]);
            if (d > maxDist) maxDist = d;
        }
    }
    return maxDist;
};

export interface ZoneNode {
    i: string; // ID
    l?: string; // Label
    t: string; // Type
    c?: ZoneNode[]; // Children
    zipCodes?: string[]; // Legacy/Explicit Zips
}

/**
 * Recursively extracts all ZIP codes from a ZoneNode.
 */
export const extractZips = (node: ZoneNode): string[] => {
    // If it's a ZIP node itself
    if (node.t === 'ZI' || node.t === 'Zip') return [node.l || node.i];

    // Legacy support for nodes with direct zipCodes array (e.g. Radius/Manual)
    if (node.zipCodes && node.zipCodes.length > 0) return node.zipCodes;

    // Recurse children
    if (node.c) {
        return node.c.flatMap(extractZips);
    }

    return [];
};

/**
 * Checks if a node tree contains a specific ID recursively.
 */
/**
 * What splitting a zone yields: its children, descending through single-child levels
 * (e.g. Bundesland Hamburg -> Stadt Hamburg -> Bezirke) so one click never returns just
 * one badge again. `parent` is the last node passed through, for labelling.
 */
export const splitZone = (node: ZoneNode): { parent: ZoneNode; children: ZoneNode[] } => {
    let parent = node;
    while (parent.c && parent.c.length === 1 && parent.c[0].c && parent.c[0].c.length > 0) {
        parent = parent.c[0];
    }
    return { parent, children: parent.c || [] };
};

export const containsId = (node: ZoneNode, targetId: string): boolean => {
    if (node.i === targetId) return true;
    if (node.c) {
        return node.c.some(child => containsId(child, targetId));
    }
    return false;
};

/**
 * Determines if a new zone should "eat" (replace) an existing zone.
 * 
 * Logic:
 * 1. If the new zone contains the existing zone by ID (classic hierarchy check).
 * 2. If the new zone covers all ZIP codes that the existing zone covers (content-based check).
 */
export const shouldEatZone = (newZone: ZoneNode, existingZone: ZoneNode): boolean => {
    // 1. ID based check
    if (containsId(newZone, existingZone.i)) return true;

    // 2. ZIP subset check
    // If the existing zone has no Zips (unlikely but possible), don't eat it unless ID matches
    const existingZips = extractZips(existingZone);
    if (existingZips.length === 0) return false;

    const newZoneZips = new Set(extractZips(newZone));

    // Check if every ZIP in the existing zone is contained in the new zone
    return existingZips.every(zip => newZoneZips.has(zip));
};

/**
 * Determines if a new zone is already covered by an existing zone (and thus should not be added).
 */
export const isZoneBlocked = (newZone: ZoneNode, existingZone: ZoneNode): boolean => {
    // 1. ID based check: existing zone contains new zone
    if (containsId(existingZone, newZone.i)) return true;

    // 2. ZIP subset check: existing zone covers all Zips of new zone
    const newZips = extractZips(newZone);
    if (newZips.length === 0) return false;

    const existingZoneZips = new Set(extractZips(existingZone));
    return newZips.every(zip => existingZoneZips.has(zip));
};

/**
 * Consolidates a list of selected zones by replacing child zones with their parents 
 * if all children of that parent are present.
 * Uses a greedy top-down approach (Country -> State -> Region -> etc).
 */
export const consolidateZones = (currentZones: ZoneNode[], allDefinitions: ZoneNode[]): ZoneNode[] => {
    // 1. Get all currently selected ZIPs
    const activeZips = new Set(currentZones.flatMap(extractZips));
    if (activeZips.size === 0) return currentZones;

    const consolidated: ZoneNode[] = [];

    // Helper to traverse and consume
    const traverse = (nodes: ZoneNode[]) => {
        const sortedNodes = [...nodes].sort((a, b) => {
            const isAZip = a.t === 'ZI' || a.t === 'Zip';
            const isBZip = b.t === 'ZI' || b.t === 'Zip';
            if (isAZip && !isBZip) return 1;
            if (!isAZip && isBZip) return -1;
            return 0;
        });

        for (const node of sortedNodes) {
            // Get zips for this definition node
            const nodeZips = extractZips(node);

            // Skip empty nodes
            if (nodeZips.length === 0) continue;

            // Check if ALL zips of this node are in activeZips
            const isComplete = nodeZips.every(zip => activeZips.has(zip));

            if (isComplete) {
                // Consume: Add this node to result
                consolidated.push(node);
                // Remove these zips from active set so we don't pick up children or overlapping zones
                for (const zip of nodeZips) activeZips.delete(zip);
            } else {
                // Not complete, check children
                if (node.c) {
                    traverse(node.c);
                }
            }
        }
    };

    traverse(allDefinitions);

    // 2. Handle remaining loose ZIPs
    // These are ZIPs that didn't fit into any full parent in the definitions.
    // We should preserve them as nodes.
    // Fix TS2802: Convert to array for ES5 compatibility
    for (const zip of Array.from(activeZips)) {
        // Try to find original node to preserve properties if possible
        const original = currentZones.find(z => {
            const zZips = extractZips(z);
            return zZips.length === 1 && zZips[0] === zip;
        });

        if (original) {
            consolidated.push(original);
        } else {
            // Fallback: create simple node
            consolidated.push({
                i: zip,
                t: 'ZI',
                l: zip
            });
        }
    }

    return consolidated;
};

// ---------------------------------------------------------------------------
// Kernzone helpers (FREE-tier scrambling)
// ---------------------------------------------------------------------------

/** Returns 'kern' if the ZIP is in the first 3 slots of allZips, else 'rand'. */
export function getZipZone(zip: string, allZips: string[]): 'kern' | 'rand' {
    return allZips.indexOf(zip) < 3 ? 'kern' : 'rand';
}

/**
 * Moves `zip` to the given kern slot (0, 1, or 2) by swapping with the
 * current occupant of that slot. All other zips remain in place.
 */
export function assignZipToKernSlot(zip: string, slot: 0 | 1 | 2, allZips: string[]): string[] {
    const idx = allZips.indexOf(zip);
    if (idx === -1 || idx === slot) return allZips;
    const result = [...allZips];
    [result[slot], result[idx]] = [result[idx], result[slot]];
    return result;
}
